import HeroData from './HeroData';

export const Team = {
  RADIANT: 'radiant',
  DIRE: 'dire'
};

class DraftState {
  constructor({
    radiantHeroes = [],
    direHeroes = [],
    radiantDrafts = null,
    direDrafts = null,
    radiantSequences = null,
    direSequences = null,
  } = {}) {
    this.radiantHeroes = radiantHeroes;
    this.direHeroes = direHeroes;
    this.radiantDrafts = radiantDrafts || [Array(24).fill('')];
    this.direDrafts = direDrafts || [Array(24).fill('')];
    this.radiantSequences = radiantSequences || [];
    this.direSequences = direSequences || [];
    
    if (this.radiantSequences.length === 0 || this.direSequences.length === 0) {
      this.initializeSequences();
    }
  }

  // Create an initial empty draft state
  static initial() {
    return new DraftState();
  }

  // Helper to get all sequence numbers for large boxes
  static getLargeBoxSequences() {
    return ['8', '9', '13', '14', '15', '16', '17', '18', '23', '24'];
  }

  // Initialize sequences for draft simulator
  initializeSequences() {
    // Initialize drafts if empty
    if (this.radiantDrafts.length === 0) {
      this.radiantDrafts.push(Array(24).fill(''));
    }
    if (this.direDrafts.length === 0) {
      this.direDrafts.push(Array(24).fill(''));
    }

    // Initialize sequences if empty
    if (this.radiantSequences.length === 0) {
      const initialRadiantSequence = Array(24).fill('');
      initialRadiantSequence[0] = '1'; // First pick
      initialRadiantSequence[1] = '4'; // Second pick
      initialRadiantSequence[2] = '7'; // Third pick
      initialRadiantSequence[3] = '8'; // Fourth pick
      initialRadiantSequence[4] = '10'; // Fifth pick
      initialRadiantSequence[5] = '11'; // Sixth pick
      initialRadiantSequence[6] = '14'; // Seventh pick
      initialRadiantSequence[7] = '15'; // Eighth pick
      initialRadiantSequence[8] = '18'; // Ninth pick
      initialRadiantSequence[9] = '19'; // Tenth pick
      initialRadiantSequence[10] = '22'; // Eleventh pick
      initialRadiantSequence[11] = '23'; // Twelfth pick
      this.radiantSequences.push(initialRadiantSequence);
    }

    if (this.direSequences.length === 0) {
      const initialDireSequence = Array(24).fill('');
      initialDireSequence[0] = '2'; // First pick
      initialDireSequence[1] = '3'; // Second pick
      initialDireSequence[2] = '5'; // Third pick
      initialDireSequence[3] = '6'; // Fourth pick
      initialDireSequence[4] = '9'; // Fifth pick
      initialDireSequence[5] = '12'; // Sixth pick
      initialDireSequence[6] = '13'; // Seventh pick
      initialDireSequence[7] = '16'; // Eighth pick
      initialDireSequence[8] = '17'; // Ninth pick
      initialDireSequence[9] = '20'; // Tenth pick
      initialDireSequence[10] = '21'; // Eleventh pick
      initialDireSequence[11] = '24'; // Twelfth pick
      this.direSequences.push(initialDireSequence);
    }
  }

  // Add a new scenario to the draft
  addScenario() {
    this.radiantDrafts.push(Array(24).fill(''));
    this.direDrafts.push(Array(24).fill(''));
    this.radiantSequences.push([...this.radiantSequences[0]]);
    this.direSequences.push([...this.direSequences[0]]);
    return this;
  }

  // Load from existing data
  loadFromData(radiantDraftsData, direDraftsData) {
    this.radiantDrafts = [...radiantDraftsData];
    this.direDrafts = [...direDraftsData];

    // Ensure sequences are updated to match the draft data
    if (this.radiantSequences.length < this.radiantDrafts.length) {
      while (this.radiantSequences.length < this.radiantDrafts.length) {
        this.radiantSequences.push([...this.radiantSequences[0]]);
        this.direSequences.push([...this.direSequences[0]]);
      }
    }
    return this;
  }

  // Update a hero in the draft
  updateHero(isRadiant, columnIndex, listIndex, heroName) {
    if (isRadiant) {
      // Ensure listIndex is within the valid range for Radiant (0-11)
      if (columnIndex < this.radiantDrafts.length && listIndex >= 0 && listIndex < 12) {
        const newRadiantDrafts = [...this.radiantDrafts];
        newRadiantDrafts[columnIndex] = [...newRadiantDrafts[columnIndex]];
        newRadiantDrafts[columnIndex][listIndex] = heroName;
        this.radiantDrafts = newRadiantDrafts;
      }
    } else {
      // For Dire heroes, map to the correct array position (12-23)
      if (columnIndex < this.direDrafts.length && listIndex >= 0 && listIndex < 12) {
        const newDireDrafts = [...this.direDrafts];
        newDireDrafts[columnIndex] = [...newDireDrafts[columnIndex]];
        // Add 12 to properly position in the 12-23 range
        newDireDrafts[columnIndex][listIndex + 12] = heroName;
        this.direDrafts = newDireDrafts;
      } else if (columnIndex < this.direDrafts.length && listIndex >= 12 && listIndex < 24) {
        // If already in 12-23 range, use as is
        const newDireDrafts = [...this.direDrafts];
        newDireDrafts[columnIndex] = [...newDireDrafts[columnIndex]];
        newDireDrafts[columnIndex][listIndex] = heroName;
        this.direDrafts = newDireDrafts;
      }
    }
    return this;
  }

  // Check if a hero is already drafted
  isHeroDrafted(heroName, { excludeColumnIndex, isRadiant = true, excludeIndex } = {}) {
    // Check in radiant drafts
    for (let i = 0; i < this.radiantDrafts.length; i++) {
      if (excludeColumnIndex !== undefined && i === excludeColumnIndex && isRadiant) {
        continue;
      }
      for (let j = 0; j < this.radiantDrafts[i].length; j++) {
        if (excludeIndex !== undefined && j === excludeIndex && i === excludeColumnIndex && isRadiant) {
          continue;
        }
        if (this.radiantDrafts[i][j] === heroName) return true;
      }
    }

    // Check in dire drafts
    for (let i = 0; i < this.direDrafts.length; i++) {
      if (excludeColumnIndex !== undefined && i === excludeColumnIndex && !isRadiant) {
        continue;
      }
      for (let j = 0; j < this.direDrafts[i].length; j++) {
        if (excludeIndex !== undefined && j === excludeIndex && i === excludeColumnIndex && !isRadiant) {
          continue;
        }
        if (this.direDrafts[i][j] === heroName) return true;
      }
    }

    return false;
  }

  // Check if the draft is complete (both teams have 5 heroes)
  get isComplete() {
    return this.radiantHeroes.length === 5 && this.direHeroes.length === 5;
  }

  // Get heroes for a specific team
  getHeroesForTeam(team) {
    return team === Team.RADIANT ? this.radiantHeroes : this.direHeroes;
  }

  // Get the count of heroes for a team
  getHeroCount(team) {
    return team === Team.RADIANT ? this.radiantHeroes.length : this.direHeroes.length;
  }

  // Get team composition stats
  getTeamStats(team) {
    const heroes = team === Team.RADIANT ? this.radiantHeroes : this.direHeroes;

    if (heroes.length === 0) {
      return {
        damage: 0,
        durability: 0,
        control: 0,
        mobility: 0,
        push: 0,
      };
    }

    // This would need to be implemented with actual hero stats
    // For now we're returning dummy values
    return {
      damage: Math.random() * 10,
      durability: Math.random() * 10,
      control: Math.random() * 10,
      mobility: Math.random() * 10,
      push: Math.random() * 10,
    };
  }
}

export default DraftState;
